import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";

export const useGymReservations = (gymId: string | null) => {
  const { data: profile } = useProfileQuery();

  return useQuery({
    queryKey: ['gym-reservations', gymId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!gymId || !user || !profile) return [];

      // Verify manager has access to this gym
      const { data: gymManager } = await supabase
        .from('gym_managers')
        .select('*')
        .eq('manager_id', user.id)
        .eq('gym_id', gymId)
        .maybeSingle();

      if (!gymManager && profile.role !== 'admin') {
        throw new Error("No tienes permisos para ver las reservas de este gimnasio");
      }

      // Get all reservations with user and item details using separate queries
      const { data: reservations, error: reservationsError } = await supabase
        .from('reservations')
        .select('*')
        .order('created_at', { ascending: false });

      if (reservationsError) throw reservationsError;
      if (!reservations) return [];

      // Get all reservation items
      const { data: items, error: itemsError } = await supabase
        .from('reservation_items')
        .select('id, name, category_id');

      if (itemsError) throw itemsError;

      // Get all reservation categories for this gym
      const { data: categories, error: categoriesError } = await supabase
        .from('reservation_categories')
        .select('id, name, gym_id')
        .eq('gym_id', gymId);

      if (categoriesError) throw categoriesError;

      // Get user profiles
      const userIds = [...new Set(reservations.map(r => r.user_id))];
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Filter and enrich reservations for this gym
      const gymReservations = reservations
        .map(reservation => {
          const item = items?.find(item => item.id === reservation.item_id);
          const category = item ? categories?.find(cat => cat.id === item.category_id) : null;
          const profile = profiles?.find(p => p.id === reservation.user_id);
          
          return category ? {
            ...reservation,
            reservation_items: {
              name: item?.name || '',
            },
            profiles: {
              username: profile?.username || '',
              first_name: profile?.first_name || '',
              last_name: profile?.last_name || '',
            }
          } : null;
        })
        .filter(Boolean);

      return gymReservations;
    },
    enabled: !!gymId && !!profile,
  });
};